import { Children, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { CheckOutlined } from '@ant-design/icons';
import { EmployeeType, Node, ProcessThirdPartyIpType } from '@prisma/client';
import { UseMutationResult } from '@tanstack/react-query';
import {
  Button,
  ButtonProps,
  Checkbox,
  Form,
  FormInstance,
  FormItemProps,
  FormProps,
  Input,
  Radio,
  ResultProps,
  Select,
  Space,
  Table,
} from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { AxiosResponse } from 'axios';

import { CreateProcessDto } from '@app/domain-interfaces';
import { Tables } from '@app/web-components';

import { EmployeeTypeRenderer } from '../../../../../common';
import { ProcessWithDetails, useFindT3Nodes } from '../data';

import { useAgsRolePicker } from './use-ags-role-picker';

type AgsRoleMap = {
  access: { label: ReactNode; value: string };
  employeeType: EmployeeType;
};

type ThirdPartyIpPattern = { id: number; type: ProcessThirdPartyIpType; pattern: string; isOptional: boolean };
type PdmPrefixPattern = {
  id: number;
  prefix: string;
  thirdPartyIp: ThirdPartyIpPattern[];
};

export type ProcessManagementFormData = {
  name: string;
  t3NodeId?: number | null;
  pdmPrefixes: PdmPrefixPattern[];
  agsRoles: AgsRoleMap[];
};

export const useProcessForm = (
  mutate: UseMutationResult<AxiosResponse<ProcessWithDetails, unknown>, unknown, CreateProcessDto, unknown>,
) => {
  const [form] = Form.useForm<ProcessManagementFormData>();
  const { getSelectProps } = useAgsRolePicker();
  const navigate = useNavigate();
  const [thirdPartyIpForm] = Form.useForm<ThirdPartyIpPattern>();
  const type = Form.useWatch('type', thirdPartyIpForm);

  const { data, isFetching } = useFindT3Nodes<Node>();

  const getAgsRolesFormItemProps = (props?: FormItemProps) => ({
    children: (
      <Tables.DataTable recordType="AGS Role" pagination={false}>
        <Form.Item
          name="access"
          label="AGS Role"
          required
          rules={[{ required: true, message: `AGS role is required` }]}
        >
          <Select {...getSelectProps()} />
        </Form.Item>
        <Form.Item
          dependencies={['access']}
          label="Employee Type"
          name="employeeType"
          rules={[
            { required: true, message: 'Employee type is required.' },
            ({ getFieldValue }: Omit<FormInstance<AgsRoleMap>, 'scrollToField' | 'getFieldInstance'>) => ({
              validator: (rule: unknown, value: EmployeeType) =>
                new Promise((resolve, reject) => {
                  const employeeType = getFieldValue('access')?.label.props.employeeType;

                  return !employeeType || employeeType === EmployeeType.ANY || value === employeeType
                    ? resolve('')
                    : reject(`Employee type must match access's employee type.`);
                }),
            }),
          ]}
        >
          <Radio.Group
            options={[
              { label: <EmployeeTypeRenderer employeeType={EmployeeType.INTEL} />, value: EmployeeType.INTEL },
              {
                label: <EmployeeTypeRenderer employeeType={EmployeeType.CONTINGENT_WORKER} />,
                value: EmployeeType.CONTINGENT_WORKER,
              },
            ]}
          />
        </Form.Item>
      </Tables.DataTable>
    ),
    label: 'AGS Roles',
    name: 'agsRoles',
    required: true,
    rules: [
      { required: true },
      Tables.noDuplicatesValidatorFactory<AgsRoleMap>(
        (a, b) => a.access.value === b.access.value && a.employeeType === b.employeeType,
      ),
    ],
    ...props,
  });

  const getCancelButtonProps = (): ButtonProps => ({
    children: 'Cancel',
    onClick: () => navigate('../'),
  });

  const getCreateAnotherButtonProps = (): ButtonProps => ({
    children: 'Create Another',
    onClick: () => {
      mutate.reset();
      form.resetFields();
    },
  });

  const getDmsNameFormItemProps = (): FormItemProps => ({
    children: <Input />,
    label: 'DMS Name',
    name: 'name',
    required: true,
    rules: [
      { required: true },
      { min: 5, message: 'Name must be at least 5 characters.' },
      { max: 25, message: 'Name must be less than 25 characters.' },
      {
        pattern: new RegExp(/^([A-Z0-9_-]+)$/),
        message: 'Can only be capital letters, numbers, underscores, or dashes',
      },
    ],
  });

  const getFormProps = (): FormProps<ProcessManagementFormData> & { children: ReactNode } => ({
    children: (
      <>
        <Form.Item {...getDmsNameFormItemProps()} />
        <Form.Item {...getT3NameFormItemProps()} />
        <Form.Item {...getPdmPrefixFormItemProps()} />
        <Form.Item {...getAgsRolesFormItemProps()} />
        <Space>
          <Button {...getSaveButtonProps()} />
          <Button {...getResetButtonProps()} />
          <Button {...getCancelButtonProps()} />
        </Space>
      </>
    ),
    form,
    layout: 'vertical',
    onFinish: (formData) => {
      const createDto: CreateProcessDto = {
        name: formData.name.trim(),
        t3NodeId: formData.t3NodeId ?? null,
        agsRoles: formData.agsRoles.map((x) => ({ externalId: x.access.value, employeeType: x.employeeType })),
        pdmPrefixes: formData.pdmPrefixes,
      };

      mutate.mutate(createDto);
    },
  });

  const getGotoProcessesButtonProps = (): ButtonProps => ({
    children: 'Go to Processes',
    type: 'primary',
    onClick: () => navigate('../'),
  });

  const getPdmPrefixFormItemProps = (): FormItemProps => ({
    children: (
      <Tables.DataTable
        expandable={{
          expandedRowRender: (record: PdmPrefixPattern) => (
            <Table
              rowKey="id"
              columns={[
                { title: 'Type', dataIndex: 'type' },
                { title: 'Pattern', dataIndex: 'pattern' },
                { title: 'Is Optional', dataIndex: 'isOptional', render: (value) => (value ? <CheckOutlined /> : '') },
              ]}
              dataSource={record.thirdPartyIp}
            />
          ),
        }}
        modalProps={{ width: 1000 }}
        recordType="PDM Prefix"
        pagination={false}
      >
        <Form.Item label="Prefix" name="prefix" required rules={[{ required: true }]}>
          <Input />
        </Form.Item>
        <Form.Item
          label="Third Party IP"
          name="thirdPartyIp"
          required
          rules={[
            { required: true },
            Tables.noDuplicatesValidatorFactory<{ type: ProcessThirdPartyIpType; pattern: string }>(
              (a, b) => a.type === b.type && a.pattern === b.pattern,
            ),
            () => ({
              validator: (rule: unknown, value: ThirdPartyIpPattern[]) =>
                new Promise((resolve, reject) =>
                  value.some((x) => x.type === ProcessThirdPartyIpType.PDK)
                    ? resolve('')
                    : reject('Please provide at least one PDK pattern'),
                ),
            }),
            () => ({
              validator: (rule: unknown, value: ThirdPartyIpPattern[]) =>
                new Promise((resolve, reject) =>
                  value.some((x) => x.type === ProcessThirdPartyIpType.STD_CELL)
                    ? resolve('')
                    : reject('Please provide at least one STD Cell pattern'),
                ),
            }),
          ]}
          tooltip="If multiple values are provided for the same pdm prefix/ip type pair, at least one must match for the user during validations"
        >
          <Tables.DataTable form={thirdPartyIpForm} pagination={false}>
            <Form.Item label="Type" name="type" required rules={[{ required: true }]}>
              <Select
                options={[
                  { label: 'PDK', value: ProcessThirdPartyIpType.PDK },
                  { label: 'STD Cell', value: ProcessThirdPartyIpType.STD_CELL },
                  { label: 'IO', value: ProcessThirdPartyIpType.IO },
                ]}
              />
            </Form.Item>
            <Form.Item label="Pattern" name="pattern" required rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item
              hidden={type !== ProcessThirdPartyIpType.IO}
              label="Is Optional"
              name="isOptional"
              valuePropName="checked"
            >
              <Checkbox />
            </Form.Item>
          </Tables.DataTable>
        </Form.Item>
      </Tables.DataTable>
    ),
    label: 'PDM Prefixes',
    name: 'pdmPrefixes',
    required: true,
    rules: [
      { required: true },
      Tables.noDuplicatesValidatorFactory<{ prefix: string }>((a, b) => a.prefix.trim() === b.prefix.trim()),
    ],
  });

  const getResetButtonProps = (): ButtonProps => ({
    children: 'Reset',
    onClick: () => form.resetFields(),
  });

  const getSaveButtonProps = (): ButtonProps => ({
    children: 'Save',
    htmlType: 'submit',
    loading: mutate.isPending,
    type: 'primary',
  });

  const getSuccessResultProps = (props?: ResultProps): ResultProps => ({
    status: 'success',
    subTitle: `Bundle name: ${mutate.data?.data.name}`,
    ...props,
    extra: [<Button {...getGotoProcessesButtonProps()} />, ...Children.toArray(props?.extra)],
  });

  const getT3NameFormItemProps = (): FormItemProps => ({
    children: (
      <Select
        loading={isFetching}
        options={data?.data.map((x): DefaultOptionType => ({ label: x.name, value: x.id })) ?? []}
      />
    ),
    label: 'T3 Node',
    name: 't3NodeId',
  });

  return {
    form,
    getAgsRolesFormItemProps,
    getCancelButtonProps,
    getCreateAnotherButtonProps,
    getDmsNameFormItemProps,
    getFormProps,
    getGotoProcessesButtonProps,
    getPdmPrefixFormItemProps,
    getResetButtonProps,
    getSaveButtonProps,
    getSuccessResultProps,
    getT3NameFormItemProps,
  };
};
